import React from "react";
import { DisconnectionFormValues } from "../../types/DisconnectionFormValues";
import styled from "styled-components";
import { mediaQueries } from "../../theme";
import { Title } from "../../components/Title";
import { Page } from "../../components/Page";
import { Grid } from "../../components/Grid";
import { Flex } from "../../components/Flex";
import { DisconnectionProgressStepper } from "../../components/DisconnectionProgressStepper";
import { Span } from "../../components/Span";
import { Link } from "../../components/Link";
import { Button } from "../../components/Button";
import { useHistory, useLocation } from "react-router-dom";
import { Line } from "../../components/Line";
import { QualtricsPlaceHolder } from "../../components/QualtricsPlaceHolder";
import { DisconnectionThankYouOrder } from "./_/DisconnectionThankYouOrder";
import { IconPopupTip } from "../../components/IconPopupTip";

interface Props {
  tabTitle: string;
}

const FlexWrapper = styled(Flex)`
  ${mediaQueries.extraLargeDevice} {
    grid-column-start: 2;
  }
`;

const GridWrapper = styled(Grid)`
  display: flex;
  flex-direction: column;

  ${mediaQueries.extraLargeDevice} {
    display: grid;
    grid-template-columns: 30vw auto 15vw;
  }
`;

const StyledOrderService = styled(Flex)`
  border: 1px solid;
  border-color: ${({ theme }) => theme.colors.neutrals.ln30};
`;

const horizontalLine = (
  <Line
    width="100%"
    my={5}
    zIndex={0}
    border="1px solid"
    borderColor="neutrals.ln30"
    bg="neutrals.ln30"
    opacity="1"
    height={0}
  />
);

export const DisconnectionThankYou: React.FC<Props> = ({ tabTitle }) => {
  const location = useLocation<DisconnectionFormValues>();

  const history = useHistory();
  const handleOrderServiceClick = () => {
    history.push("/service-type");
  };

  return (
    <Page tabTitle={tabTitle}>
      <DisconnectionProgressStepper currentStep={4} />

      <GridWrapper mx={[2, 2, 4, 4, 0]}>
        <FlexWrapper flexDirection="column">
          <Title as="h1" lineHeight={7} fontWeight="400" fontSize={7}>
            Thank you for your order
          </Title>
          <p>
            Please note you will receive a confirmation email for your order.
          </p>
          <p>
            Once we have assessed your order, we will let you know the timeframe
            to complete the job and any associated costs.
          </p>

          <Flex
            justifyContent="space-between"
            alignItems="center"
            mt={4}
            mb={5}
          >
            <Title as="h2" my={0} lineHeight={5} fontWeight="500" fontSize={5}>
              Your order(s)
            </Title>

            <Flex alignItems="center">
              <IconPopupTip
                label="Type of service"
                icon="help"
                alignItems="flex-start"
              >
                A permanent disconnection (decommission) of your power supply
                when demolishing / relocating a building or removing temporary
                supply and permanent supply is not required
              </IconPopupTip>
            </Flex>
          </Flex>

          <DisconnectionThankYouOrder
            id={"disconnection-thank-you-order"}
            order={location.state}
          />

          {horizontalLine}

          <Span mb={5}>
            If you have any questions, please contact us on{" "}
            <Link href="tel:0508 832 867">0508 832 867</Link>.
          </Span>

          <StyledOrderService
            px={4}
            py={3}
            borderRadius="infoSection"
            flexDirection="column"
          >
            <Span fontSize={4} lineHeight={4} mb={3}>
              Do you want to order another service?
            </Span>

            <Span>
              You can keep ordering other services through the button below.
            </Span>

            <Flex marginRight="auto">
              <Button
                iconLeft="add"
                variant="primary"
                magnitude="medium"
                mt={3}
                data-testid="order-service-button"
                onClick={handleOrderServiceClick}
              >
                Order service
              </Button>
            </Flex>
          </StyledOrderService>

          {horizontalLine}

          <Flex flexDirection="column">
            <Title
              as="h2"
              mb={3}
              mt={0}
              lineHeight={5}
              fontWeight="400"
              fontSize={5}
            >
              Feedback
            </Title>
            <QualtricsPlaceHolder />
          </Flex>
        </FlexWrapper>
      </GridWrapper>
    </Page>
  );
};
